import { ReactNode } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Square, SquareCheckBig } from 'lucide-react';

import { DataTableColumnHeader } from '../DataTable';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import {
  STEP_EVENT_STATUS_LABEL,
  formatGateArrivalStart,
  formatScheduledAt,
  formatTrackedStepTime,
  YardGateEntry,
} from '../../adapter';
import type {
  YardGateAppointmentStatus,
  YardGateTrackedStepColumn,
  YardGateMutableStatus,
  YardGateTrackedStep,
} from 'contracts/YardGate';
import { statusVisuals } from '../../statusVisuals';
import * as S from '../../styles';
import { DockCell } from './DockCell';
import type { DockOption } from './DockCell';
import { PhoneCell } from './PhoneCell';
import { ObservationCell } from './ObservationCell';
import { AnnotationCell } from './AnnotationCell';
import { PhysicalDockBadge } from './PhysicalDockBadge';

export { statusVisuals } from '../../statusVisuals';

const ClampText = ({ children }: { children: ReactNode }) => (
  <S.ClampText>{children}</S.ClampText>
);

const displayTime = (value: string | null) => value ?? '—';
const displayOptionalText = (value: string) =>
  value && value !== '-' ? value : '—';

const EntryTypeText = ({ type, registrationType }: Pick<YardGateEntry, 'type' | 'registrationType'>) => (
  <ClampText>
    {registrationType === 'HORA_CERTA'
      ? 'HORA CERTA'
      : type === 'ORDER'
        ? 'ORDEM'
        : 'AVULSO'}
  </ClampText>
);

const OrderTypeIndicator = ({ type }: { type: string | null }) => {
  if (!type) {
    return <ClampText>—</ClampText>;
  }

  const isUnloading = type
    .toLocaleUpperCase('pt-BR')
    .startsWith('DESCARREGAMENTO');

  return (
    <S.OrderTypeBadge role="img" aria-label={type} title={type}>
      {isUnloading ? 'D' : 'C'}
    </S.OrderTypeBadge>
  );
};

const StatusIndicator = ({ status }: { status: YardGateAppointmentStatus }) => {
  const { label, color } = statusVisuals[status];
  return (
    <S.CellCenteredContent>
      <S.StatusDot role="img" aria-label={label} title={label} color={color} />
    </S.CellCenteredContent>
  );
};

const GateArrivalCell = ({ entry }: { entry: YardGateEntry }) => {
  const gateArrival = formatGateArrivalStart(entry);

  return <ClampText>{gateArrival === 'N/A' ? '—' : gateArrival}</ClampText>;
};

const ChecklistStepCell = ({ step }: { step: YardGateTrackedStep | null }) => {
  if (!step) {
    return <ClampText>—</ClampText>;
  }

  const hasFinishedAt = step.finishedAt != null;
  const finishedAt = formatTrackedStepTime(step.finishedAt, 'dd/MM/yy HH:mm');
  const label = `${step.name}: ${STEP_EVENT_STATUS_LABEL[step.status]}${
    hasFinishedAt ? ` · Fim: ${finishedAt}` : ''
  }`;

  return (
    <S.ChecklistStepCell role="img" aria-label={label} title={label}>
      <S.ChecklistStepIcon $checked={hasFinishedAt}>
        {hasFinishedAt ? (
          <SquareCheckBig aria-hidden="true" size={16} />
        ) : (
          <Square aria-hidden="true" size={16} />
        )}
      </S.ChecklistStepIcon>
      {hasFinishedAt ? <span>{finishedAt}</span> : null}
    </S.ChecklistStepCell>
  );
};

interface ActionsCellProps {
  entry: YardGateEntry;
  isBusy: boolean;
  onPrintOrder: (entry: YardGateEntry) => void;
  onStatusChange: (
    type: YardGateEntry['type'],
    sourceId: number,
    status: YardGateMutableStatus
  ) => void;
  onAddObservation: (entry: YardGateEntry) => void;
  onManageChecklist: (entry: YardGateEntry) => void;
  onStartJourney: (entry: YardGateEntry) => void;
}

const READ_ONLY_ACTION_STATUSES = new Set<YardGateAppointmentStatus>([
  'EXIT_AUTHORIZED',
  'CANCELLED',
  'NO_SHOW',
]);

const OBSERVATION_ACTION_STATUSES = new Set<YardGateAppointmentStatus>([
  'PENDING',
  'AWAITING_RELEASE',
  'ENTRY_AUTHORIZED',
]);

const ActionsCell = ({
  entry,
  isBusy,
  onPrintOrder,
  onStatusChange,
  onAddObservation,
  onManageChecklist,
  onStartJourney,
}: ActionsCellProps) => {
  const isReadOnly = READ_ONLY_ACTION_STATUSES.has(entry.status);
  const portariaStep =
    entry.flow?.trackedSteps.find((step) => step.type === 'PORTARIA') ?? null;
  const showStartJourney =
    entry.type === 'ORDER' && entry.orderId != null && !isReadOnly;
  const canStartJourney =
    showStartJourney &&
    (entry.hasPortariaStep
      ? portariaStep?.status !== 'COMPLETED'
      : entry.flow == null);
  const canPrintOrder = !isReadOnly && entry.orderId != null;
  const canAuthorizeEntry =
    entry.status === 'AWAITING_RELEASE' ||
    (entry.status === 'PENDING' && entry.flow != null);
  const hasPhysicalDockForEntryRelease =
    entry.type !== 'ORDER' || entry.docks.length > 0;
  const canAuthorizeExit = entry.status === 'ENTRY_AUTHORIZED';
  const canAddAnnotation =
    OBSERVATION_ACTION_STATUSES.has(entry.status) &&
    !entry.annotation &&
    !entry.observationAudit.updatedBy;
  const hasOtherActions =
    canPrintOrder || canAuthorizeEntry || canAuthorizeExit;
  const changeStatus = (status: YardGateMutableStatus) =>
    onStatusChange(entry.type, entry.sourceId, status);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Ações de ${entry.plate}`}
          disabled={isBusy}
          className="h-6 w-6 p-0"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white min-w-[170px]">
        {showStartJourney && (
          <DropdownMenuItem
            disabled={isBusy || !canStartJourney}
            onSelect={() => onStartJourney(entry)}
          >
            Iniciar jornada
          </DropdownMenuItem>
        )}
        {showStartJourney && <DropdownMenuSeparator />}
        <DropdownMenuItem
          disabled={isBusy}
          onSelect={() => onManageChecklist(entry)}
        >
          Anexos
        </DropdownMenuItem>
        {(hasOtherActions || canAddAnnotation) && <DropdownMenuSeparator />}
        {canPrintOrder && (
          <DropdownMenuItem
            disabled={isBusy}
            onSelect={() => onPrintOrder(entry)}
          >
            Imprimir Ordem
          </DropdownMenuItem>
        )}
        {canPrintOrder && (canAuthorizeEntry || canAuthorizeExit) && (
          <DropdownMenuSeparator />
        )}
        {canAuthorizeEntry && (
          <DropdownMenuItem
            disabled={isBusy || !hasPhysicalDockForEntryRelease}
            onSelect={() => changeStatus('ENTRY_AUTHORIZED')}
          >
            Liberar Entrada
          </DropdownMenuItem>
        )}
        {canAuthorizeExit && (
          <DropdownMenuItem
            disabled={isBusy}
            onSelect={() => changeStatus('EXIT_AUTHORIZED')}
          >
            Liberar Saída
          </DropdownMenuItem>
        )}
        {canAddAnnotation && hasOtherActions && <DropdownMenuSeparator />}
        {canAddAnnotation && (
          <DropdownMenuItem
            disabled={isBusy}
            onSelect={() => onAddObservation(entry)}
          >
            Adicionar anotação
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const fixedColumns: ColumnDef<YardGateEntry>[] = [
  {
    id: 'Status',
    accessorFn: (entry) => entry.status,
    header: 'Status',
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    size: 88,
    minSize: 88,
    maxSize: 88,
    cell: ({ row }) => <StatusIndicator status={row.original.status} />,
  },
  {
    id: 'Ord. Coleta',
    accessorFn: (entry) => entry.orderNumber,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Ordem" />
    ),
    meta: { label: 'Ordem' },
    size: 120,
    cell: ({ row }) => (
      <span className="inline-flex min-h-5 items-center gap-2">
        <ClampText>{displayOptionalText(row.original.orderNumber)}</ClampText>
        <PhysicalDockBadge
          docks={row.original.docks}
          highlighted={row.original.dockAttentionCount >= 2}
        />
      </span>
    ),
  },
  {
    id: 'Tipo',
    accessorFn: (entry) => entry.type,
    header: 'Tipo',
    enableSorting: false,
    cell: ({ row }) => (
      <EntryTypeText
        type={row.original.type}
        registrationType={row.original.registrationType}
      />
    ),
  },
  {
    id: 'Data Agendamento',
    accessorFn: (entry) => entry.scheduledAt,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data Agendamento" />
    ),
    cell: ({ row }) => <ClampText>{formatScheduledAt(row.original)}</ClampText>,
  },
  {
    id: 'CPF',
    accessorFn: (entry) => entry.driverCpf,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="CPF" />
    ),
    enableSorting: false,
    cell: ({ row }) => <ClampText>{row.original.driverCpf}</ClampText>,
  },
  {
    id: 'Motorista',
    accessorFn: (entry) => entry.driverName,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Motorista" />
    ),
    cell: ({ row }) => <ClampText>{row.original.driverName}</ClampText>,
  },
  {
    id: 'P. Chegada',
    header: 'P. Chegada',
    enableSorting: false,
    cell: ({ row }) => <GateArrivalCell entry={row.original} />,
  },
  {
    id: 'H. Entrada',
    accessorFn: (entry) => entry.startTime,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="H. Entrada" />
    ),
    cell: ({ row }) => (
      <ClampText>{displayTime(row.original.startTime)}</ClampText>
    ),
  },
  {
    id: 'H. Saída',
    accessorFn: (entry) => entry.endTime,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="H. Saída" />
    ),
    cell: ({ row }) => (
      <ClampText>{displayTime(row.original.endTime)}</ClampText>
    ),
  },
  {
    id: 'Placa',
    accessorFn: (entry) => entry.plate,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Placa" />
    ),
    cell: ({ row }) => <ClampText>{row.original.plate}</ClampText>,
  },
  {
    id: 'Carreta',
    accessorFn: (entry) => entry.trailerPlate,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Carreta" />
    ),
    enableSorting: false,
    cell: ({ row }) => (
      <ClampText>{displayTime(row.original.trailerPlate)}</ClampText>
    ),
  },
  {
    id: 'Transp./Prest. Serviço',
    accessorFn: (entry) => entry.carrierName,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Transp./Prest. Serviço" />
    ),
    cell: ({ row }) => (
      <ClampText>{displayOptionalText(row.original.carrierName)}</ClampText>
    ),
  },
  {
    id: 'Cliente',
    accessorFn: (entry) => entry.customerName,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Cliente" />
    ),
    cell: ({ row }) => (
      <ClampText>{displayOptionalText(row.original.customerName)}</ClampText>
    ),
  },
  {
    id: 'Armazém',
    accessorFn: (entry) => entry.warehouseName,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Armazém" />
    ),
    enableSorting: false,
    cell: ({ row }) => (
      <ClampText>{displayOptionalText(row.original.warehouseName)}</ClampText>
    ),
  },
  {
    id: 'NF',
    accessorFn: (entry) => entry.invoiceNumber,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="NF" />
    ),
    enableSorting: false,
    cell: ({ row }) => (
      <ClampText>{row.original.invoiceNumber ?? '—'}</ClampText>
    ),
  },
  {
    id: 'Tipo de ordem',
    accessorFn: (entry) => entry.orderType,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Tipo de ordem" />
    ),
    cell: ({ row }) => <OrderTypeIndicator type={row.original.orderType} />,
  },
];

const buildTrackedStepColumns = (
  trackedStepColumns: YardGateTrackedStepColumn[]
): ColumnDef<YardGateEntry>[] =>
  trackedStepColumns.map((column) => ({
    id: `tracked-step-${column.code}`,
    accessorFn: (entry) =>
      entry.flow?.trackedSteps.find((step) => step.code === column.code) ??
      null,
    header: () => (
      <span
        title={column.occurrences
          .map(({ flowName, sequence }) => `${flowName}: etapa ${sequence}`)
          .join('\n')}
      >
        {column.name}
      </span>
    ),
    enableSorting: false,
    meta: { label: column.name },
    cell: ({ getValue }) => (
      <ChecklistStepCell step={getValue() as YardGateTrackedStep | null} />
    ),
  }));

interface YardGateColumnsOptions {
  trackedStepColumns: YardGateTrackedStepColumn[];
  isBusy: boolean;
  onPrintOrder: (entry: YardGateEntry) => void;
  onStatusChange: (
    type: YardGateEntry['type'],
    sourceId: number,
    status: YardGateMutableStatus
  ) => void;
  dockOptions: DockOption[];
  onUpdateDocks: (entry: YardGateEntry, docks: number[]) => Promise<boolean>;
  onUpdatePhone: (
    entry: YardGateEntry,
    phone: string | null
  ) => Promise<boolean>;
  onAddObservation: (entry: YardGateEntry) => void;
  onManageChecklist: (entry: YardGateEntry) => void;
  onStartJourney: (entry: YardGateEntry) => void;
}

export const filterDocksByWarehouse = (
  dockOptions: DockOption[],
  warehouseId: number | null
): DockOption[] =>
  warehouseId === null
    ? []
    : dockOptions.filter((option) => option.warehouseId === warehouseId);

export const buildYardGateColumns = ({
  trackedStepColumns,
  isBusy,
  onPrintOrder,
  onStatusChange,
  dockOptions,
  onUpdateDocks,
  onUpdatePhone,
  onAddObservation,
  onManageChecklist,
  onStartJourney,
}: YardGateColumnsOptions): ColumnDef<YardGateEntry>[] => {
  const areaColumn: ColumnDef<YardGateEntry> = {
    id: 'Área',
    accessorFn: (entry) => entry.area.map(({ name }) => name).join(', '),
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Área" />
    ),
    cell: ({ row }) => (
      <ClampText>
        {displayOptionalText(
          row.original.area.map(({ name }) => name).join(', ')
        )}
      </ClampText>
    ),
  };
  const docaAgendamentoColumn: ColumnDef<YardGateEntry> = {
    id: 'Doca Agendamento',
    accessorFn: (entry) => entry.dock,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Doca Agendamento" />
    ),
    cell: ({ row }) => (
      <ClampText>{displayOptionalText(row.original.dock ?? '')}</ClampText>
    ),
  };
  const docaColumn: ColumnDef<YardGateEntry> = {
    id: 'Doca física',
    accessorFn: (entry) => entry.docks.map(({ name }) => name).join(', '),
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Doca física" />
    ),
    cell: ({ row }) => {
      const { warehouseId } = row.original;
      return (
        <DockCell
          entry={row.original}
          options={filterDocksByWarehouse(dockOptions, warehouseId)}
          isBusy={isBusy}
          onSave={(docks) => onUpdateDocks(row.original, docks)}
        />
      );
    },
  };
  const phoneColumn: ColumnDef<YardGateEntry> = {
    id: 'Telefone',
    accessorFn: (entry) => entry.phone,
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Telefone" />
    ),
    cell: ({ row }) => (
      <PhoneCell
        entry={row.original}
        isBusy={isBusy}
        onSave={(phone) => onUpdatePhone(row.original, phone)}
      />
    ),
  };
  const authorizerColumn: ColumnDef<YardGateEntry> = {
    id: 'Autorizado por',
    accessorFn: (entry) => entry.entryAuthorizedByName,
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Autorizado por" />
    ),
    cell: ({ row }) => (
      <ClampText>
        {displayOptionalText(row.original.entryAuthorizedByName)}
      </ClampText>
    ),
  };
  const observationColumn: ColumnDef<YardGateEntry> = {
    id: 'Observações',
    accessorFn: (entry) => entry.observation,
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Observações" />
    ),
    cell: ({ row }) => <ObservationCell entry={row.original} />,
  };
  const annotationColumn: ColumnDef<YardGateEntry> = {
    id: 'Anotações',
    accessorFn: (entry) => entry.annotation,
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Anotações" />
    ),
    cell: ({ row }) => <AnnotationCell entry={row.original} />,
  };
  const actionsColumn: ColumnDef<YardGateEntry> = {
    id: 'Ações',
    header: 'Ações',
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    cell: ({ row }) => (
      <ActionsCell
        entry={row.original}
        isBusy={isBusy}
        onPrintOrder={onPrintOrder}
        onStatusChange={onStatusChange}
        onAddObservation={onAddObservation}
        onManageChecklist={onManageChecklist}
        onStartJourney={onStartJourney}
      />
    ),
  };
  const motoristaIndex = fixedColumns.findIndex(
    (column) => column.id === 'Motorista'
  );
  const withAuthorizer = [
    ...fixedColumns.slice(0, motoristaIndex + 1),
    authorizerColumn,
    ...fixedColumns.slice(motoristaIndex + 1),
  ];
  const authorizerIndex = withAuthorizer.findIndex(
    (column) => column.id === 'Autorizado por'
  );
  const withPhone = [
    ...withAuthorizer.slice(0, authorizerIndex + 1),
    phoneColumn,
    ...withAuthorizer.slice(authorizerIndex + 1),
  ];
  const pChegadaIndex = withPhone.findIndex(
    (column) => column.id === 'P. Chegada'
  );
  const withDoca = [
    ...withPhone.slice(0, pChegadaIndex + 1),
    docaAgendamentoColumn,
    docaColumn,
    ...withPhone.slice(pChegadaIndex + 1),
  ];
  const docaIndex = withDoca.findIndex(
    (column) => column.id === 'Doca Agendamento'
  );

  return [
    ...withDoca.slice(0, docaIndex),
    areaColumn,
    ...withDoca.slice(docaIndex),
    ...buildTrackedStepColumns(trackedStepColumns),
    observationColumn,
    annotationColumn,
    actionsColumn,
  ];
};
